import React, { useContext, useEffect, useMemo, useState } from 'react';
import './DashboardAdmin.css';
import WelcomeCard from './partials/WelcomeCard';
import { Clock, CheckCircle2, FileText, XCircle, ChevronDown, Filter } from 'lucide-react';
import StatisticsChart from './partials/StatisticsChart';
import { AuthContext } from '../../context/AuthContext';
import Preloader from '../../components/Preloader';
import TopVendedoresChart from './partials/TopVendedoresChart';
import TopProductosChart from './partials/TopProductosChart';
import SalesEvolutionChart from './partials/SalesEvolutionChart';
import ProvincesRanking from './partials/ProvincesRanking';
import RecaudacionMensualChart from './partials/RecaudacionMensualChart';
import { useDashboardMetrics } from '../../hooks/useDashboardMetrics';
import MetricCard from './partials/MetricCard';

const getNombreCompleto = (usuario) => {
  if (!usuario) return '';
  return [usuario.nombre, usuario.apellidoPaterno, usuario.apellidoMaterno]
    .filter(Boolean).join(' ').trim();
};

const DashboardAdmin = () => {
  const { usuario, token } = useContext(AuthContext);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [estadoSeleccionado, setEstadoSeleccionado] = useState('');
  const [filtroSupervisor, setFiltroSupervisor] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const { stats, loading, error, estados, supervisores, chartsData, fetchData } = useDashboardMetrics(token);
  const { regiones, topVendedores, porProductos, evolucionMensual, porProvincias, recaudacionMensual } = chartsData;

  const tienePermisoRecaudacion = useMemo(() =>
    usuario?.permisos?.includes("list_venta"),
    [usuario]);

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => {
      fetchData(fechaInicio, fechaFin, estadoSeleccionado, filtroSupervisor);
    }, 300);
    return () => clearTimeout(timer);
  }, [token, fechaInicio, fechaFin, estadoSeleccionado, filtroSupervisor, fetchData]);


  if (loading) return <Preloader />;
  if (error) return <div className="error-container">{error}</div>;

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-grid">
        <div className="left-column">
          <WelcomeCard usuario={usuario} total={stats?.total} />

          <div className="metrics-row">
            <MetricCard
              title="Ventas Pendientes"
              icon={Clock}
              count={stats?.pendientes}
              total={stats?.total}
              color="#EAB308"
            />

            <MetricCard
              title="Ventas Instaladas"
              icon={CheckCircle2}
              count={stats?.instaladas}
              total={stats?.total}
              color="#22C55E"
            />

            <MetricCard
              title="Ventas Reportadas"
              icon={FileText}
              count={stats?.reportadas}
              total={stats?.total}
              color="#3B82F6"
            />

            <MetricCard
              title="Ventas Anuladas"
              icon={XCircle}
              count={stats?.anuladas}
              total={stats?.total}
              color="#EF4444"
            />
          </div>

          <div className="center-column">
            <SalesEvolutionChart data={evolucionMensual || []} />
            <div className={`charts-row ${tienePermisoRecaudacion ? '' : 'full-width-province'}`}>

              <ProvincesRanking data={porProvincias || []} />

              {tienePermisoRecaudacion && (
                <RecaudacionMensualChart data={recaudacionMensual || []} />
              )}

            </div>
          </div>

        </div>
        <div className="right-column">
          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            <ChevronDown className={`arrow ${showFilters ? 'up' : ''}`} size={18} />
          </button>

          <div className={`filter-collapsible ${showFilters ? 'open' : ''}`}>
            <div className="filter-bar-vertical">
              {/* Fecha Desde */}
              <div className="filter-item">
                <label htmlFor="fecha-desde">Desde</label>
                <input
                  id="fecha-desde"
                  type="date"
                  className="modern-input"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>

              {/* Fecha Hasta */}
              <div className="filter-item">
                <label htmlFor="fecha-hasta">Hasta</label>
                <input
                  id="fecha-hasta"
                  type="date"
                  className="modern-input"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>

              {/* Estado */}
              <div className="filter-item">
                <label htmlFor="fecha-estado">Estado</label>
                <select
                  id="fecha-estado"
                  className="modern-select"
                  value={estadoSeleccionado}
                  onChange={(e) => setEstadoSeleccionado(e.target.value)}
                >
                  <option value="">Todos los estados</option>
                  {estados.map((estado) => (
                    <option key={estado.id} value={estado.id}>
                      {estado.descripcion}
                    </option>
                  ))}
                </select>
              </div>

              {/* Supervisor */}
              <div className="filter-item"> {/* Corregido: filtro-item → filter-item */}
                <label htmlFor="filtroJefe">Filtrar por Supervisor:</label>
                <select
                  id="filtroJefe"
                  value={filtroSupervisor}
                  onChange={(e) => setFiltroSupervisor(e.target.value)}
                  className="filtro-select"
                >
                  <option value="">Todos los supervisores</option>
                  {supervisores.map((supervisor) => (
                    <option key={supervisor.id} value={supervisor.id}>
                      {getNombreCompleto(supervisor)}
                    </option>
                  ))}
                </select>
              </div>

              <button
                className="btn-reset"
                onClick={() => {
                  setFechaInicio('');
                  setFechaFin('');
                  setEstadoSeleccionado('');
                  setFiltroSupervisor('')
                }}
              >
                Limpiar
              </button>
            </div>
          </div>


          <TopVendedoresChart data={topVendedores || []} />
          <TopProductosChart data={porProductos || []} />
          <StatisticsChart regionesData={regiones} />
        </div>
      </div>
    </div >
  );
};

export default DashboardAdmin;